// EventsAdmin.js
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Modal, Button, Form } from "react-bootstrap";
import api from "../api";
import EventDetails from "./EventDetails";
import EventSidebar from "./EventSidebar";
import EventUrlsCard from "./EventUrlsCard";
import EventStatsCard from "./EventStatsCard";
import SocialStats from "./SocialStats";
import EventPhotosStats from "./EventPhotosStats";

const EventsAdmin = ({ onLogout, user, selectedE }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [typeList, setTypeList] = useState([
    { type_id: 1, type_name: "wedding" },
    { type_id: 2, type_name: "bar mitsva" },
    { type_id: 3, type_name: "bat mitsva" },
    { type_id: 4, type_name: "birthday" },
    { type_id: 5, type_name: "brit milah" },
    { type_id: 6, type_name: "business" },
    { type_id: 7, type_name: "other" },
  ]);

  const getEmptyEvent = () => ({
    name: "",
    id_name: "",
    date: "",
    city: "",
    country: "",
    type_id: "",
    event_room: "",
    user_id: user?.user_id ?? null,
  });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events?user_id=${user.user_id}`, {
        withCredentials: true,
      });
      setEvents(res.data);
      // Select the first event by default if none is selected
      if (!selectedEvent && res.data.length > 0) {
        setSelectedEvent(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeEvent = async (event) => {
    if (
      window.confirm(
        `Are you sure you want to delete the event "${event.name}"?`
      )
    ) {
      try {
        await api.delete(`/event/${event.event_id}`, { withCredentials: true });
        setEvents(events.filter((ev) => ev.event_id !== event.event_id));
        setSelectedEvent(null);
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  useEffect(() => {
    if (user?.user_id) fetchEvents();
  }, [user]);

  useEffect(() => {
    if (selectedE) {
      const event = events.find((ev) => ev.id_name === selectedE);
      if (event) {
        setSelectedEvent(event);
      }
    }
  }, [selectedE, events]);

  const filteredEvents = events.filter((ev) =>
    ev.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="mt-3">
      
        <Row>
          <Col md={3} className="border-end">
            <EventSidebar
              events={filteredEvents}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            onSelectEvent={setSelectedEvent}
            selectedEvent={selectedEvent}
            onNewEvent={() => setNewEvent(getEmptyEvent())}
          />
        </Col>
        {selectedEvent ? (<Col md={9} className="p-4">
          {loading ? (
            <div>Loading events...</div>
          ) : (
            <>
              {selectedEvent && (
                <h4 className="mb-3 text-secondary fw-bold">
                  {selectedEvent.name}
                </h4>
              )}

              <EventPhotosStats id_name={selectedEvent?.id_name} storage={selectedEvent?.storage} />

              <EventStatsCard stats={selectedEvent?.stats || {}} />
              <SocialStats stats={selectedEvent?.social_stats || {}} />
              <EventUrlsCard
                urls={{
                  gallery: `https://pic-loud.com/event/${selectedEvent?.id_name}/gallery`,
                  magicFinder: `https://pic-loud.com/event/${selectedEvent?.id_name}`,
                }}
              />

              <EventDetails
                event={selectedEvent}
                typeList={typeList}
                onEdit={setEditingEvent}
                onDelete={removeEvent}
              />
            </>
          )}
        </Col> ) : (
        <div className="text-center text-muted py-4">
          <p className="mb-0 fs-5">
            {events?.length === 0 ? "Create a new event to start" : "Select an event to view its details"}
          </p>
        </div>
      )}
      </Row>
     

      {/* Edit Modal */}
      <Modal show={!!editingEvent} onHide={() => setEditingEvent(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingEvent && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editingEvent.name}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={editingEvent.city}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, city: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  value={editingEvent.country}
                  onChange={(e) =>
                    setEditingEvent({
                      ...editingEvent,
                      country: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={editingEvent.date}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, date: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Room</Form.Label>
                <Form.Control
                  value={editingEvent.event_room || ""}
                  onChange={(e) =>
                    setEditingEvent({
                      ...editingEvent,
                      event_room: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={editingEvent.type_id || ""}
                  onChange={(e) =>
                    setEditingEvent({
                      ...editingEvent,
                      type_id: parseInt(e.target.value),
                    })
                  }
                >
                  <option value="">Select a type</option>
                  {typeList.map((type) => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.type_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingEvent(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await api.put(`/event/${editingEvent.event_id}`, editingEvent, {
                withCredentials: true,
              });
              setEditingEvent(null);
              fetchEvents();
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Modal */}
      <Modal show={!!newEvent} onHide={() => setNewEvent(null)}>
        <Modal.Header closeButton>
          <Modal.Title>New Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newEvent && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={newEvent.name}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>ID Name</Form.Label>
                <Form.Control
                  value={newEvent.id_name}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, id_name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={newEvent.city}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, city: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  value={newEvent.country}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, country: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={newEvent.date}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, date: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Room</Form.Label>
                <Form.Control
                  value={newEvent.event_room}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, event_room: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={newEvent.type_id}
                  onChange={(e) =>
                    setNewEvent({
                      ...newEvent,
                      type_id: parseInt(e.target.value),
                    })
                  }
                >
                  <option value="">Select type</option>
                  {typeList.map((type) => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.type_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setNewEvent(null)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={async () => {
              try {
                await api.post(`/event`, newEvent, { withCredentials: true });
                setNewEvent(null);
                fetchEvents();
              } catch (err) {
                console.error("Failed to create event", err);
                alert("Creation failed");
              }
            }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EventsAdmin;
